import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@workos-inc/authkit-react';
import { fetchFragmentData, fragmentErrorMessage } from '~portal/lib';

/**
 * @desc An insights data fragment (`/fragments/d/*`, round 3 lane 6) read for an SPA component that
 * draws the data itself: fetched with the user's token through the same-origin `/fragments`
 * rewrite, then validated by `decode` against the route's shared `Fragments` schema. `data` is
 * `null` for the route's 404 (or a `null` body), `undefined` until it answers.
 *
 * Not retried: a 401/403 must not loop, and the caller shows its usual inline error, whose message
 * is the fragment-failure copy for a gate refusal.
 */
export type FragmentDataQuery<T> = {
  data: T | null | undefined;
  isLoading: boolean;
  error: { message: string } | null;
};

export function useFragmentData<T>({
  path,
  decode,
  enabled = true,
  staleTime,
}: {
  /** The data route's filled path — also the query key, since one path has one decoder. */
  path: string;
  /** May be async: a decoder can load its schema lazily, off the entry chunk. */
  decode: (body: unknown) => T | null | Promise<T | null>;
  enabled?: boolean;
  /** How long an answer stays fresh in the query cache (insights caches it too). */
  staleTime: number;
}): FragmentDataQuery<T> {
  const { getAccessToken } = useAuth();
  // The SDK swaps in a new function once its client initialises; the data does not depend on which
  // one fetched it, so it stays out of the key and is read through a ref.
  const tokenRef = useRef(getAccessToken);
  useEffect(() => {
    tokenRef.current = getAccessToken;
  }, [getAccessToken]);
  const query = useQuery({
    queryKey: ['fragments', path],
    queryFn: async ({ signal }) =>
      decode(await fetchFragmentData(path, { getAccessToken: () => tokenRef.current(), signal })),
    enabled,
    retry: false,
    staleTime,
  });
  return {
    data: query.data,
    isLoading: enabled && query.isPending,
    error: query.error ? { message: fragmentErrorMessage(query.error) } : null,
  };
}
